import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { BiData, TopNavMenuClickBiEvent } from '@wix/da-bi/pkg/events';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import { useFakeToggleState } from '@wix/da-hooks/pkg/useFakeToggleState';
import type { MenuItem } from '@wix/da-ds/pkg/Dropdown/MenuList';
import { pushUserSettingsModal } from '../../../../Modals/redux/actionCreators';
import {
  getContentFilter,
  getShowMatureContent,
} from '../../../../publicSession/selectors';
import { toggleMature } from '../../../../publicSession/actions';
import SuffixText from '../_partials/SuffixText';

export function useContentFilterItem(): MenuItem & { key: string } {
  const { t } = useTranslation();
  const contentFilter = useSelector(getContentFilter) ?? 'standard';
  const dispatchPushUserSettingsModal = useAction(pushUserSettingsModal);
  return {
    key: 'content-filter',
    label: t('siteHeader.userMenu.contentFilter'),
    suffix: (
      <SuffixText>
        {t(`siteHeader.userMenu.contentFilter.${contentFilter}`)}
      </SuffixText>
    ),
    onClick: dispatchPushUserSettingsModal,
    biData: BiData<TopNavMenuClickBiEvent>({
      evid: 702,
      menu_item: 'content_filter',
      menu_name: 'profile',
      toggle_value: contentFilter,
    }),
  };
}

// note: they kept the old toggle here even after 3 lvls of contentFilter
// were added because they thought it was better to send folks to sisu
//
// This is only used in the logged out menu
export function useFakeContentFilterItem(): MenuItem & { key: string } {
  const dispatchToggleMature = useAction(toggleMature);
  const shouldShowMatureContent = useSelector(getShowMatureContent) ?? false;
  const { onClick, toggleState } = useFakeToggleState(shouldShowMatureContent);
  const { t } = useTranslation();

  const handleClick = useCallback(() => {
    onClick();
    dispatchToggleMature(shouldShowMatureContent);
  }, [shouldShowMatureContent, onClick, dispatchToggleMature]);

  return {
    key: 'fake-content-filter',
    label: t('siteHeader.userMenu.matureContent'),
    size: 'medium',
    onChange: handleClick,
    checked: toggleState,
    biData: BiData<TopNavMenuClickBiEvent>({
      evid: 702,
      menu_item: 'toggle_mature',
      menu_name: 'profile',
      toggle_value: !shouldShowMatureContent ? 'mature_on' : 'mature_off',
    }),
  };
}
